<script setup>
import { useI18n } from 'vue-i18n'
import { useNavBarSearch } from '../../composables/useNavBarSearch'
import M3Menu from '../M3Menu.vue'

defineProps({
  isMobileOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['closeMobileSearch'])

const { t } = useI18n()

const {
  searchQuery,
  searchScope,
  advancedParams,
  searchInputRef,
  searchScopeOptions,
  getPlaceholder,
  debouncedSearch,
  handleManualSearch,
  clearSearch
} = useNavBarSearch()

const handleSearch = (event) => {
  handleManualSearch(event, () => {}, () => emit('closeMobileSearch'))
}

const closeMobileSearch = () => {
  emit('closeMobileSearch')
}
</script>

<template>
  <div :class="[
    'flex-1 items-center min-w-0 transition-all duration-300',
    isMobileOpen ? 'absolute inset-0 z-50 bg-brand-surface px-4 flex h-full' : 'hidden sm:flex mx-2 sm:mx-4'
  ]">
    <!-- Back Button (Mobile Only) -->
    <button
      v-if="isMobileOpen"
      @click="closeMobileSearch"
      class="mr-3 text-brand-muted hover:text-brand-text sm:hidden"
      :aria-label="t('common.back')"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <div class="relative w-full flex items-center bg-m3-surface-variant rounded-xl sm:rounded-2xl border border-m3-outline focus-within:border-m3-primary focus-within:ring-2 focus-within:ring-m3-primary/20 focus-within:shadow-lg focus-within:shadow-m3-primary/5 transition-all duration-300">
      <!-- Scope Dropdown -->
      <M3Menu
        v-model="searchScope"
        :options="searchScopeOptions"
        :label="t('nav.search_scope')"
        width="140px"
        class="flex-shrink-0"
      />

      <!-- Standard Search Input -->
      <input
        v-if="searchScope !== 'album_advanced'"
        ref="searchInputRef"
        v-model="searchQuery"
        @keyup.enter="handleSearch"
        @input="debouncedSearch"
        type="text"
        :placeholder="getPlaceholder"
        :aria-label="t('nav.search')"
        data-search-input
        class="w-full min-w-0 bg-transparent text-brand-text placeholder-brand-muted/70 font-medium text-base sm:text-sm py-2 pl-2 sm:pl-3 pr-16 sm:pr-20 focus:outline-none rounded-r-lg sm:rounded-r-full"
      />

      <!-- Advanced Album Inputs -->
      <div v-else class="flex flex-1 items-center px-1 sm:px-2 gap-1 sm:gap-2 min-w-0">
        <input
          v-model="advancedParams.artist"
          @keyup.enter="handleSearch"
          @input="debouncedSearch"
          type="text"
          :placeholder="t('search.artist')"
          :aria-label="t('search_filters.artist_label')"
          class="flex-1 min-w-0 bg-brand-surface text-brand-text placeholder-brand-muted/70 text-xs sm:text-sm py-1 px-2 rounded focus:outline-none border border-brand-muted/30 focus:border-brand-accent"
        />
        <input
          v-model="advancedParams.title"
          @keyup.enter="handleSearch"
          @input="debouncedSearch"
          type="text"
          :placeholder="t('album_form.title_label')"
          :aria-label="t('album_form.title_label')"
          class="flex-1 min-w-0 bg-brand-surface text-brand-text placeholder-brand-muted/70 text-xs sm:text-sm py-1 px-2 rounded focus:outline-none border border-brand-muted/30 focus:border-brand-accent"
        />
      </div>

      <!-- Clear Button -->
      <button
        v-if="searchQuery && searchScope !== 'album_advanced'"
        @click="clearSearch"
        :aria-label="t('search.clear_aria')"
        class="absolute right-8 sm:right-10 top-0 h-full px-2 text-brand-muted hover:text-brand-text focus:text-brand-teal transition-colors flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- Search Button -->
      <button
        @click="handleSearch"
        :aria-label="t('nav.search')"
        class="absolute right-0 top-0 h-full px-2 sm:px-3 text-brand-muted hover:text-brand-accent flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    </div>
  </div>
</template>